import React from 'react'
import { useLocation } from 'react-router-dom'
import Layout from '../../components/layout/Layout'
import { Badge } from '../../components/ui'
import { cn } from '../../shared/lib/cn'
import { useVariableLagRun } from './api'
import { formatCurrency } from './utils'

const TITLES: Record<string, string> = {
  '/variable-lag-accruals': 'Close Review',
  '/variable-lag-accruals/review': 'HITL Review',
  '/variable-lag-accruals/sources': 'Source Evidence',
  '/variable-lag-accruals/exports': 'NetSuite Exports',
  '/variable-lag-accruals/configuration': 'Configuration',
}

export default function VariableLagAccrualsLayout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const viewTitle = TITLES[pathname] ?? 'Close Review'
  const { data } = useVariableLagRun({
    enabled: pathname !== '/variable-lag-accruals/configuration',
  })

  return (
    <Layout>
      <div className="flex h-full flex-col bg-gray-50 dark:bg-dark-bg">
        <div className="border-b border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
          <div className="px-6 py-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-semibold text-charcoal dark:text-gray-100">
                    Variable Lag Accruals
                  </h1>
                  <Badge variant="secondary">{viewTitle}</Badge>
                  <Badge variant="secondary">EC-02d</Badge>
                  {data && <Badge variant="warning">{data.summary.hitl_required_count} HITL</Badge>}
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Variable and lag accruals for {data?.period_label ?? 'month-end close'}
                </p>
              </div>

              {data && (
                <div className="grid min-w-[520px] grid-cols-4 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 text-sm dark:border-dark-border dark:bg-dark-border max-lg:min-w-full max-sm:grid-cols-2">
                  <RibbonMetric label="Total" value={formatCurrency(data.summary.grand_total, true)} tone="critical" />
                  <RibbonMetric label="JE" value={data.exports.journal_entry.balanced ? 'Balanced' : 'Open'} />
                  <RibbonMetric label="Rows" value={`${data.exports.journal_entry.rows}`} />
                  <RibbonMetric label="Period" value={data.period} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-6 py-6">
          {children}
        </div>
      </div>
    </Layout>
  )
}

function RibbonMetric({
  label,
  value,
  tone = 'neutral',
}: {
  label: string
  value: string
  tone?: 'neutral' | 'critical'
}) {
  return (
    <div className="bg-white px-4 py-3 dark:bg-dark-surface">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div
        className={cn(
          'mt-0.5 truncate text-base font-semibold tabular-nums',
          tone === 'critical'
            ? 'text-rose-600 dark:text-rose-400'
            : 'text-charcoal dark:text-gray-100',
        )}
      >
        {value}
      </div>
    </div>
  )
}
